import { Handle, Position } from '@xyflow/react';
import { Stack, TextInput, Select, Button, Group, Paper, ActionIcon, Switch } from '@mantine/core';
import { IconGitBranch, IconPlus, IconTrash } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps, HandleDefinition } from '../types';
import { conditionalWidgetCore, type ConditionalConfig, type Condition } from '@legacy-shared/widgets/core/conditional';

export { type ConditionalConfig, type Condition } from '@legacy-shared/widgets/core/conditional';

const operatorOptions = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
];

function ConditionalNode({ id, data, selected }: WidgetNodeProps<ConditionalConfig>) {
  const conditionCount = data.config.conditions.length;
  const preview = conditionCount > 0 ? `${conditionCount} condition${conditionCount > 1 ? 's' : ''}` : 'No conditions';
  const outputs = getDynamicOutputs(data.config);

  return (
    <WidgetNodeShell
      icon={IconGitBranch}
      title={data.label || 'Conditional'}
      subtitle={preview}
      configured={conditionCount > 0}
      errors={data.errors}
      selected={selected}
      color="orange"
      minWidth={Math.max(180, outputs.length * 70)}
    >
      <Handle type="target" position={Position.Top} id="in" />
      {outputs.map((o, index) => (
        <LabeledHandle
          key={o.id}
          type="source"
          position={o.position}
          id={o.id}
          label={o.label}
          offset={`${((index + 1) / (outputs.length + 1)) * 100}%`}
          nodeId={id}
        />
      ))}
    </WidgetNodeShell>
  );
}

function ConditionalConfigPanel({ config, onChange }: ConfigPanelProps<ConditionalConfig>) {
  const addCondition = () => {
    const newCondition: Condition = {
      id: `condition_${Date.now()}`,
      variable: '',
      operator: 'equals',
      value: '',
      label: `Condition ${config.conditions.length + 1}`,
    };
    onChange({ ...config, conditions: [...config.conditions, newCondition] });
  };

  const updateCondition = (index: number, updates: Partial<Condition>) => {
    onChange({ ...config, conditions: config.conditions.map((c, i) => (i === index ? { ...c, ...updates } : c)) });
  };

  const removeCondition = (index: number) => {
    const newConditions = config.conditions.filter((_, i) => i !== index);
    onChange({ ...config, conditions: newConditions });
  };

  return (
    <Stack gap="md">
      {config.conditions.map((condition, index) => (
        <Paper key={condition.id} p="sm" withBorder>
          <Stack gap="xs">
            <Group justify="space-between">
              <TextInput
                size="xs"
                placeholder="Label"
                value={condition.label}
                onChange={(e) => updateCondition(index, { label: e.target.value })}
                style={{ flex: 1 }}
              />
              <ActionIcon color="red" variant="subtle" onClick={() => removeCondition(index)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
            <TextInput
              size="xs"
              label="Variable"
              placeholder="e.g., lastDigit"
              value={condition.variable}
              onChange={(e) => updateCondition(index, { variable: e.target.value })}
            />
            <Select
              size="xs"
              label="Operator"
              value={condition.operator}
              onChange={(value) => updateCondition(index, { operator: value || 'equals' })}
              data={operatorOptions}
            />
            {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
              <TextInput
                size="xs"
                label="Value"
                placeholder="Expected value"
                value={condition.value}
                onChange={(e) => updateCondition(index, { value: e.target.value })}
              />
            )}
          </Stack>
        </Paper>
      ))}

      <Button leftSection={<IconPlus size={16} />} variant="light" onClick={addCondition}>
        Add Condition
      </Button>

      <Switch
        label="Default Branch"
        description="Add a default output for when no conditions match"
        checked={config.defaultBranch}
        onChange={(e) => onChange({ ...config, defaultBranch: e.currentTarget.checked })}
      />
    </Stack>
  );
}

function getDynamicOutputs(config: ConditionalConfig): HandleDefinition[] {
  const outputs: HandleDefinition[] = config.conditions.map((condition, index) => ({
    id: condition.id,
    type: 'condition' as const,
    label: condition.label || `#${index + 1}`,
    position: 'bottom' as const,
  }));

  if (config.defaultBranch) {
    outputs.push({
      id: 'default',
      type: 'default' as const,
      label: 'Default',
      position: 'bottom' as const,
    });
  }

  return outputs;
}

export const conditionalWidget: WidgetDefinition<ConditionalConfig> = {
  ...conditionalWidgetCore,
  icon: 'IconGitBranch',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [],
  },

  getDynamicOutputs,

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: ConditionalNode,
    configPanel: ConditionalConfigPanel,
    preview: (config) => {
      const count = config.conditions.length;
      return count > 0 ? `${count} condition${count > 1 ? 's' : ''}` : 'No conditions';
    },
  },

  validation: {
    ...conditionalWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
